import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';
import { fetchQuery } from '@/utils/fetchQuery';
import { GET_SEARCH_RESULT } from '@/lib/queries/search/querySearch';
import Search2LineIcon from 'remixicon-react/Search2LineIcon';
import { LoadingSpinner } from '@/components/Icons';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import PostCard from '@/components/layout/search/PostCard';
import SymbolCard from '@/components/layout/search/SymbolCard';
import { SearchPosts, SearchResult, Symbol } from '@/types/search/searchTypes';
import ExchangeCard from '@/components/layout/search/ExchangeCard';
import { useSearchModalStore } from '@/stores/layout/searchModalStore';
import { sitePath } from '@/utils/links/safeHref';

// UPDATED: Added mapping for both hyphen and underscore just in case
const translations: Record<string, string> = {
    post: 'مقاله',
    symbol: 'ارز دیجیتال',
    gold: 'طلا و سکه',
    currency: 'ارز',
    news: 'اخبار',
    broker: 'بروکر',
    gold_platforms: 'سایت‌های طلا', // Matches graphql field name
    'gold-platforms': 'سایت‌های طلا', // Matches WP post type slug
    prop: 'پراپ',
    exchange: 'صرافی',
    analyses: 'تحلیل',
};

interface SearchComponentProps {
    closeModal: React.Dispatch<React.SetStateAction<boolean>> | ((open: boolean) => void);
}

const SearchComponent = ({ closeModal }: SearchComponentProps) => {
    const { initialQuery, isOpen, closeSearch } = useSearchModalStore();
    const [searchQuery, setSearchQuery] = useState<string>(initialQuery || '');
    const [results, setResults] = useState<SearchPosts | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedType, setSelectedType] = useState<string>('');
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Auto-focus input when modal opens
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Update searchQuery if initialQuery changes
    useEffect(() => {
        if (initialQuery) {
            setSearchQuery(initialQuery);
        }
    }, [initialQuery]);

    // Separate debounce definition inside useMemo to ensure it's not re-created on each render
    const debouncedSearch = useMemo(() => {
        return debounce(async (query: string) => {
            if (!query.trim()) {
                setResults(null);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const cleanQuery = query.trim();

                // 1. Fetch from WordPress GraphQL
                const wpPromise = fetchQuery<SearchResult, { search: string }>(GET_SEARCH_RESULT, {
                    search: cleanQuery,
                }).catch(() => null);

                // 2. Fetch Gold and Currency from MarketPulse API (/api/coin/currencies/)
                const goldPromise = fetch(
                    `/api/coin/currencies/?type=gold&search=${encodeURIComponent(cleanQuery)}`,
                )
                    .then((res) => (res.ok ? res.json() : null))
                    .catch(() => null);

                const currencyPromise = fetch(
                    `/api/coin/currencies/?type=currency&search=${encodeURIComponent(cleanQuery)}`,
                )
                    .then((res) => (res.ok ? res.json() : null))
                    .catch(() => null);

                const [wpData, goldData, currData] = await Promise.all([
                    wpPromise,
                    goldPromise,
                    currencyPromise,
                ]);

                const searchPosts: SearchPosts = wpData?.searchPosts
                    ? { ...wpData.searchPosts }
                    : {
                          analyses: [],
                          broker: [],
                          exchange: [],
                          gold_platforms: [],
                          news: [],
                          post: [],
                          prop: [],
                          symbol: [],
                          gold: [],
                          currency: [],
                      };

                if (!searchPosts.gold) searchPosts.gold = [];
                if (!searchPosts.currency) searchPosts.currency = [];

                // Format gold models from MarketPulse
                const apiGoldModels: any[] = goldData?.data?.models || [];
                const formattedGold: Symbol[] = apiGoldModels.map((m: any) => ({
                    postType: 'gold',
                    price: m.price || 0,
                    slug: m.slug || m.symbol || '',
                    title: m.name_fa || m.name || '',
                    nameFa: m.name_fa || '',
                    uri: sitePath('symbol', m.slug || m.symbol) ?? '/markets/',
                    iconUrl: m.iconUrl || '',
                    sparkline: [],
                    usdt_price: m.price || 0,
                    price_usd: m.price_usd,
                    quote_currency: m.quote_currency,
                    change: typeof m.change_24h === 'number' ? m.change_24h : m.change || 0,
                    typeId: 3,
                }));

                // Format currency models from MarketPulse
                const apiCurrModels: any[] = currData?.data?.models || [];
                const formattedCurr: Symbol[] = apiCurrModels.map((m: any) => ({
                    postType: 'currency',
                    price: m.price || 0,
                    slug: m.slug || m.symbol || '',
                    title: m.name_fa || m.name || '',
                    nameFa: m.name_fa || '',
                    uri: sitePath('symbol', m.slug || m.symbol) ?? '/markets/',
                    iconUrl: m.iconUrl || '',
                    sparkline: [],
                    usdt_price: m.price || 0,
                    change: typeof m.change_24h === 'number' ? m.change_24h : m.change || 0,
                    typeId: 2,
                }));

                // Filter out false-positive currencies from WordPress where title/nameFa doesn't match query
                const qLower = cleanQuery.toLowerCase();
                const filteredWpCurrency = (searchPosts.currency || []).filter((item) => {
                    const t = (item.title || '').toLowerCase();
                    const n = (item.nameFa || '').toLowerCase();
                    const s = (item.slug || '').toLowerCase();
                    return t.includes(qLower) || n.includes(qLower) || s.includes(qLower);
                });

                const filteredWpGold = (searchPosts.gold || []).filter((item) => {
                    const t = (item.title || '').toLowerCase();
                    const n = (item.nameFa || '').toLowerCase();
                    const s = (item.slug || '').toLowerCase();
                    return t.includes(qLower) || n.includes(qLower) || s.includes(qLower);
                });

                // Merge without duplicates by slug
                const existingGoldSlugs = new Set(formattedGold.map((g) => g.slug.toLowerCase()));
                filteredWpGold.forEach((item) => {
                    if (!existingGoldSlugs.has((item.slug || '').toLowerCase())) {
                        formattedGold.push(item);
                    }
                });

                const existingCurrSlugs = new Set(formattedCurr.map((c) => c.slug.toLowerCase()));
                filteredWpCurrency.forEach((item) => {
                    if (!existingCurrSlugs.has((item.slug || '').toLowerCase())) {
                        formattedCurr.push(item);
                    }
                });

                searchPosts.gold = formattedGold;
                searchPosts.currency = formattedCurr;

                setResults(searchPosts);
            } catch (error) {
                console.error('Error fetching search results:', error);
            } finally {
                setLoading(false);
            }
        }, 400);
    }, []);

    const handleSearch = useCallback(
        (query: string) => {
            debouncedSearch(query);
        },
        [debouncedSearch],
    );

    useEffect(() => {
        if (searchQuery) {
            handleSearch(searchQuery);
        } else {
            setResults(null);
        }
    }, [searchQuery, handleSearch]);

    const handleClick = () => {
        closeModal(false);
        closeSearch();
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('close-mobile-menu'));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const currencySlugs = new Set([
        'usd',
        'eur',
        'gbp',
        'aed',
        'try',
        'jpy',
        'aud',
        'cad',
        'chf',
        'cny',
        'sek',
        'nok',
        'dkk',
        'kwd',
        'omr',
        'rub',
        'brl',
        'thb',
        'afn',
        'inr',
        'myr',
        'gel',
        'pkr',
        'azn',
        'iqd',
        'sar',
        'qar',
        'dollar',
        'euro',
        'pound',
        'derham',
        'lira',
        'yen',
        'yuan',
        'ruble',
        'dinar',
        'rial',
    ]);

    const goldSlugs = new Set([
        'gold',
        'coin',
        'bahar',
        'emami',
        'nim',
        'rob',
        'gerami',
        'sekkeh',
        'mesghal',
        '18k',
        '24k',
        'habab',
        'tala',
        'sikka',
        'sikkeh',
        'melted',
    ]);

    function getSymbolTypeKey(item: any): string {
        const postType = (item.postType || '').toLowerCase();
        if (postType === 'currency') return 'currency';
        if (postType === 'gold') return 'gold';

        const slug = (item.slug || item.symbol || '').toLowerCase();
        const uri = (item.uri || '').toLowerCase();

        if (currencySlugs.has(slug) || uri.includes('/currencies/') || uri.includes('/currency/')) {
            return 'currency';
        }

        if (
            goldSlugs.has(slug) ||
            uri.includes('/gold/') ||
            uri.includes('/gold-') ||
            uri.includes('-gold')
        ) {
            return 'gold';
        }

        return 'symbol';
    }

    const groupByPostType = (data: SearchPosts) => {
        const groupedResults: { [key: string]: any[] } = {};
        Object.keys(data).forEach((key) => {
            const postItems = data[key as keyof SearchPosts];
            postItems?.forEach((item) => {
                let typeKey = item.postType || key;

                if (key === 'gold' || typeKey === 'gold') {
                    typeKey = 'gold';
                } else if (key === 'currency' || typeKey === 'currency') {
                    typeKey = 'currency';
                } else if (typeKey === 'symbol' || key === 'symbol') {
                    typeKey = getSymbolTypeKey(item);
                }

                if (!groupedResults[typeKey]) {
                    groupedResults[typeKey] = [];
                }
                groupedResults[typeKey].push({
                    ...item,
                    postType: typeKey,
                });
            });
        });
        return groupedResults;
    };

    const handleButtonClick = (postType: string) => {
        setSelectedType(postType === selectedType ? '' : postType);
    };

    const renderGroupedButtons = () => {
        if (!results) return null;
        const grouped = groupByPostType(results);

        return (
            <div className='flex gap-2 my-4 overflow-x-scroll'>
                <button
                    onClick={() => handleButtonClick('')}
                    className={`px-2 py-1 rounded-lg bg-gray-100 border border-gray-200 text-black dark:bg-white/5 dark:border-white/10 dark:text-white ${
                        selectedType === ''
                            ? '!bg-primary-active !text-white dark:!bg-progress'
                            : ''
                    }`}
                >
                    همه
                </button>
                {Object.keys(grouped).map((postType) => (
                    <button
                        key={postType}
                        onClick={() => handleButtonClick(postType)}
                        className={`px-2 py-1 rounded-lg bg-gray-100 border text-nowrap border-gray-200 text-black dark:bg-white/5 dark:border-white/10 dark:text-white ${
                            selectedType === postType
                                ? '!bg-primary-active !text-white dark:!bg-progress'
                                : ''
                        }`}
                    >
                        {translations[postType] || postType}
                    </button>
                ))}
            </div>
        );
    };

    const renderGroupedResults = () => {
        if (!results) return null;

        const grouped = groupByPostType(results);
        return Object.keys(grouped).map((postType) => (
            <div
                key={postType}
                className={selectedType && selectedType !== postType ? 'hidden' : ''}
            >
                <div className={'border-b flex dark:border-white/20 my-5'}>
                    <h3 className='text-xl border-b-2 pb-1 border-primary-active dark:border-progress font-semibold'>
                        {translations[postType] || postType}
                    </h3>
                </div>
                <ScrollArea dir={'rtl'} className='flex flex-col px-0 whitespace-nowrap'>
                    <div
                        className={`flex w-full scroll-snap-x snap-mandatory pb-4 ${selectedType && selectedType === postType ? 'max-lg:flex-col flex-wrap gap-3 justify-start' : 'gap-3'}`}
                    >
                        {grouped[postType].map((item, index) => {
                            // Logic to decide which card to render
                            const isPost = ['post', 'news', 'analyses'].includes(item.postType);
                            const isSymbol = ['symbol', 'gold', 'currency'].includes(item.postType);
                            // UPDATED: Added 'gold-platforms' (hyphen) and 'gold_platforms' (underscore)
                            const isExchange = [
                                'broker',
                                'prop',
                                'exchange',
                                'gold_platforms',
                                'gold-platforms',
                            ].includes(item.postType);

                            if (isPost) {
                                return (
                                    <PostCard
                                        action={handleClick}
                                        active={
                                            selectedType && selectedType === postType
                                                ? 'true'
                                                : 'false'
                                        }
                                        key={index}
                                        item={item}
                                    />
                                );
                            } else if (isSymbol) {
                                return (
                                    <SymbolCard
                                        action={handleClick}
                                        active={
                                            selectedType && selectedType === postType
                                                ? 'true'
                                                : 'false'
                                        }
                                        key={index}
                                        item={item}
                                    />
                                );
                            } else if (isExchange) {
                                return (
                                    <ExchangeCard
                                        action={handleClick}
                                        childItem={item}
                                        key={index}
                                        index={index}
                                    />
                                );
                            } else {
                                return <div key={index}></div>;
                            }
                        })}
                    </div>
                    <ScrollBar orientation='horizontal' />
                </ScrollArea>
            </div>
        ));
    };

    return (
        <>
            <div className={'relative'}>
                <input
                    ref={searchInputRef}
                    type='text'
                    value={searchQuery}
                    placeholder='جستجو برای ....'
                    onChange={handleChange}
                    className='bg-opacity h-14 w-full rounded-lg border border-primary dark:border-white/20 bg-gray-100 border-opacity-25 pr-10 text-sm dark:bg-white/5 placeholder:text-primary/60 focus:outline-none dark:placeholder:text-white/70'
                />
                <div className='absolute inset-y-0 right-0 mr-2 flex items-center'>
                    <Search2LineIcon />
                </div>
                {loading && (
                    <LoadingSpinner className='mx-auto absolute end-3 top-5 size-5 animate-spin text-white' />
                )}
            </div>
            {renderGroupedButtons()}
            <div className={'max-h-[70vh] pb-5 lg:max-h-[70vh] overflow-y-auto'}>
                {renderGroupedResults()}
            </div>
        </>
    );
};

export default SearchComponent;
